import type { HTMLAttributes } from 'react'
import { memo } from 'react'

import type { ImageOptions } from './useImage'
import { useImage } from './useImage'
import type { SourceOptions } from './useSources'
import { useSources } from './useSources'

interface Props extends HTMLAttributes<HTMLPictureElement> {
  sources: SourceOptions[]
  img: ImageOptions
  shouldCompress?: boolean
  preload?: boolean
  sizes?: string
}

function Picture({
  sources: sourceOptions,
  img: imgProps,
  shouldCompress = true,
  sizes,
  className,
  ...otherProps
}: Props) {
  const compressedSources = useSources(sourceOptions)
  const sources = shouldCompress ? compressedSources : sourceOptions
  const compressedImg = useImage(imgProps)
  const imgAttributes = shouldCompress ? compressedImg : imgProps
  const resolvedSizes = sizes ?? '100vw'
  const imgClassName =
    [imgAttributes.className, className].filter(Boolean).join(' ') || undefined

  return (
    <picture>
      {sources.map(({ srcSet, src, media, height }, index) => {
        return (
          <source
            key={index}
            srcSet={shouldCompress ? srcSet : src}
            media={media}
            height={height}
            type={shouldCompress ? 'image/webp' : undefined}
          />
        )
      })}
      <img
        {...imgAttributes}
        {...otherProps}
        data-src={imgProps.src}
        alt={imgProps.alt}
        data-id={imgProps['data-id']}
        className={imgClassName}
        sizes={resolvedSizes}
      />
    </picture>
  )
}

export default memo(Picture)
